import { NEW_MENU_COPY as COPY } from '../../constants/newMenu';
import { familiesOfRecentLessons } from '../../utils/familyOrder';

export const NEW_ACTION = Object.freeze({
  LESSON: 'lesson',
  FAMILY: 'family',
  STUDENT: 'student',
  INVOICE: 'invoice',
  PAYMENT: 'payment',
  EXAM: 'exam',
});

const RECENT_LESSON_FAMILIES = 2;
const UNPAID_FAMILY_LIMIT = 3;
const RECENT_LESSON_HINTS = [COPY.lastLesson, COPY.previousLesson];

const fullName = (person) => `${person.first_name || ''} ${person.last_name || ''}`.trim();
const initials = (person) => `${person.first_name?.[0] || ''}${person.last_name?.[0] || ''}`.toUpperCase();
const familyItem = (family, hint) => ({
  key: `family-${family.family_id}`, label: COPY.family(fullName(family)), hint, payload: family, initials: initials(family),
});
const otherFamily = { key: 'other-family', label: COPY.otherFamily, payload: null };

/** Most recently created family: `created_at` when present, else the highest id. */
const newestFamily = (families) => families.reduce((best, family) => {
  if (!best) return family;
  const byDate = String(family.created_at || '').localeCompare(String(best.created_at || ''));
  return byDate > 0 || (byDate === 0 && family.family_id > best.family_id) ? family : best;
}, null);

const recentLessonItems = (families, students) => familiesOfRecentLessons(families, students, RECENT_LESSON_FAMILIES)
  .map((family, index) => familyItem(family, RECENT_LESSON_HINTS[index]));

function familySuggestionItems(families, students, lastPayment, unpaidFamilies) {
  const byId = new Map(families.map((family) => [String(family.family_id), family]));
  const items = new Map(recentLessonItems(families, students).map((item) => [String(item.payload.family_id), item]));
  const add = (id, hint) => {
    const key = String(id);
    const family = byId.get(key);
    if (!family) return;
    const existing = items.get(key);
    if (existing) existing.hint = `${existing.hint} · ${hint}`;
    else items.set(key, familyItem(family, hint));
  };
  if (lastPayment) add(lastPayment.family_id, COPY.lastPayment);
  const unpaidIds = new Set();
  [...unpaidFamilies].sort((a, b) => Number(b.total_due) - Number(a.total_due)).forEach((row) => {
    const id = String(row.family_id);
    if (unpaidIds.size >= UNPAID_FAMILY_LIMIT || unpaidIds.has(id) || !byId.has(id) || !(Number(row.total_due) > 0)) return;
    unpaidIds.add(id);
    add(id, COPY.unpaidLessons);
  });
  return [...items.values(), otherFamily];
}

/**
 * « + » menu sections, in display order. A section with `items` opens a sub-page (`prompt` = its question);
 * one without runs `action` directly. Each item's `payload` is the student / family to preselect (`null` = let the teacher pick).
 */
export function buildNewMenu({ suggestions = [], families = [], students = [], lastPayment = null, unpaidFamilies = [] }) {
  const latest = newestFamily(families);
  return [
    {
      action: NEW_ACTION.LESSON,
      label: COPY.lesson,
      description: suggestions.length ? COPY.suggested(suggestions.length) : COPY.lessonHint,
      prompt: COPY.lessonPrompt,
      items: suggestions.length ? [
        ...suggestions.map((s) => ({
          key: `student-${s.student_id}`, label: fullName(s), hint: s.suggestion_reason, payload: s, initials: initials(s),
        })),
        { key: 'other-student', label: COPY.otherStudent, payload: null },
      ] : null,
    },
    {
      action: NEW_ACTION.STUDENT,
      label: COPY.newStudent,
      description: COPY.newStudentHint,
      prompt: COPY.newStudentPrompt,
      items: [
        ...(latest ? [familyItem(latest, COPY.newestFamily)] : []),
        ...(families.length > 1 || !latest ? [otherFamily] : []),
        { key: 'add-family', label: COPY.addFamily, payload: null, action: NEW_ACTION.FAMILY },
      ],
    },
    {
      action: NEW_ACTION.INVOICE,
      label: COPY.invoice,
      description: COPY.invoiceHint,
      prompt: COPY.invoicePrompt,
      items: familySuggestionItems(families, students, lastPayment, unpaidFamilies),
    },
    {
      action: NEW_ACTION.PAYMENT,
      label: COPY.payment,
      description: COPY.paymentHint,
      prompt: COPY.paymentPrompt,
      items: familySuggestionItems(families, students, null, unpaidFamilies),
    },
    { action: NEW_ACTION.EXAM, label: COPY.exam, description: COPY.examHint, items: null },
  ];
}
